import { useId, useState } from 'react'
import type { FormEvent, ReactElement } from 'react'
import { Navigate, useLocation, useNavigate, Link } from 'react-router'
import { readRedirectTarget } from '../components/protected-route'
import type { RedirectState } from '../components/protected-route'
import { authErrorMessageKey } from '../lib/auth-client'
import type { AuthFailure } from '../lib/auth-client'
import { useTranslate } from '../lib/i18n-provider'
import { useSession } from '../lib/session-context'

/**
 * Inlogscherm: e-mailadres en wachtwoord tegen `POST /auth/login`.
 *
 * Drie afloopmogelijkheden, en alle drie moeten zichtbaar verschillen:
 * ingelogd (door naar waar de bezoeker heen wilde), tweede factor nodig (door
 * naar /mfa), of mislukt (melding, formulier blijft staan).
 *
 * Er staat geen letterlijke interfacetekst in dit bestand (§17.8). Het technische
 * detail van een fout staat wél onvertaald op het scherm: dat is diagnose-
 * informatie voor wie de storing moet oplossen, geen interfacetekst — dezelfde
 * afweging als op de systeempagina.
 */
export function LoginPage(): ReactElement {
  const t = useTranslate()
  const navigate = useNavigate()
  const location = useLocation()
  const { state, login } = useSession()

  const emailId = useId()
  const passwordId = useId()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<AuthFailure | null>(null)

  // Waar de bezoeker heen wilde voordat de beschermde route hem hierheen stuurde.
  const target = readRedirectTarget(location.state)

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    setBusy(true)
    setFailure(null)

    // Het e-mailadres wordt getrimd, het wachtwoord niet: spaties aan de randen
    // van een wachtwoord zijn tekens als alle andere en mogen niet stil sneuvelen.
    const result = await login(email.trim(), password)
    setBusy(false)

    if (!result.ok) {
      setFailure(result.error)
      return
    }
    if (result.data.kind === 'mfa-required') {
      void navigate('/mfa', { replace: true, state: { from: target } satisfies RedirectState })
      return
    }
    void navigate(target, { replace: true })
  }

  // Zolang de sessie nog wordt opgehaald: niets tonen dat meteen weer verdwijnt.
  if (state.phase === 'loading') {
    return (
      <p className="page__status" role="status">
        {t('common.state.loading')}
      </p>
    )
  }

  // Al ingelogd — bijvoorbeeld na een handmatige navigatie naar /login.
  if (state.phase === 'authenticated') {
    return <Navigate to={target} replace />
  }

  // Een mislukte sessiecontrole (API onbereikbaar) is geen inlogfout, maar de
  // bezoeker moet hem wél zien: anders lijkt het straks zijn wachtwoord. Tijdens
  // een lopende poging verdwijnt de melding, zodat een oude fout niet naast een
  // nieuw antwoord blijft staan.
  const shownFailure = busy ? null : (failure ?? state.error ?? null)

  return (
    <section className="page" aria-labelledby="login-heading">
      <h1 className="page__title" id="login-heading">
        {t('dashboard.auth.login.title')}
      </h1>

      {shownFailure !== null && (
        <div className="notice notice--error" role="alert">
          <p className="notice__title">{t(authErrorMessageKey(shownFailure, 'login'))}</p>
          {/* Technisch detail, bewust onvertaald. */}
          <code className="notice__detail">{shownFailure.message}</code>
        </div>
      )}

      <form
        className="auth-form"
        onSubmit={(event) => {
          void handleSubmit(event)
        }}
      >
        <div className="auth-form__field">
          <label className="auth-form__label" htmlFor={emailId}>
            {t('dashboard.auth.login.email')}
          </label>
          <input
            autoComplete="username"
            className="auth-form__input"
            disabled={busy}
            id={emailId}
            name="email"
            onChange={(event) => {
              setEmail(event.target.value)
            }}
            required
            type="email"
            value={email}
          />
        </div>

        <div className="auth-form__field">
          <label className="auth-form__label" htmlFor={passwordId}>
            {t('dashboard.auth.login.password')}
          </label>
          <input
            autoComplete="current-password"
            className="auth-form__input"
            disabled={busy}
            id={passwordId}
            name="password"
            onChange={(event) => {
              setPassword(event.target.value)
            }}
            required
            type="password"
            value={password}
          />
        </div>

        <div className="auth-form__actions">
          <button className="button" disabled={busy} type="submit">
            {busy ? t('dashboard.auth.login.submitting') : t('dashboard.auth.login.submit')}
          </button>
          {/* Zonder deze link is het herstelpad onbereikbaar, en dan bestaat het
              alleen op papier — precies de fout die regel 11 beschrijft. */}
          <Link className="auth-form__link" to="/forgot">
            {t('dashboard.auth.login.forgot')}
          </Link>
        </div>
      </form>
    </section>
  )
}
